/**
 * TanStack Query hooks for the SubContractorHub Stage Mapping tab (SCH-2.6).
 *
 * Mirrors useFieldMappings (SCH-2.1) conventions:
 *   - Direct cache splice on single-row mutations (no full refetch).
 *   - 5-min staleTime + refetchOnWindowFocus on the parent list query.
 *
 * SCH-2.6 supersedes SCH-2.2: `useReorderStageMappings` is removed (1:1
 * lookup has no order); two new hooks `useStageVocabulary` and
 * `useDiscoverStageValues` power the SCH stage-string discovery flow.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { stageMappingService } from '@/services/integrations/stageMappingService';
import type {
  ListStageMappingsResponse,
  StageVocabulary,
} from '@/types/integrations/stageMapping';

const FIVE_MINUTES = 5 * 60 * 1000;

function stageMappingsQueryKey(tenantId: string | undefined) {
  return ['subcontractorhub', tenantId, 'stage-mappings'] as const;
}

function stageVocabularyQueryKey(tenantId: string | undefined) {
  return ['subcontractorhub', tenantId, 'stage-vocabulary'] as const;
}

function defaultLandingStageQueryKey(tenantId: string | undefined) {
  return ['subcontractorhub', tenantId, 'default-landing-stage'] as const;
}

export function useStageMappings() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useQuery({
    queryKey: stageMappingsQueryKey(tenantId),
    queryFn: async () => {
      const result = await stageMappingService.listStageMappings();
      if (result.error) {
        const err = new Error(result.error.message) as Error & {
          code?: string;
          details?: Record<string, unknown>;
        };
        err.code = result.error.code;
        err.details = result.error.details;
        throw err;
      }
      return result.data;
    },
    enabled: !!tenantId,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
  });
}

export function useCreateStageMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = stageMappingsQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: stageMappingService.createStageMapping,
    // UPSERT: replace if existing externalStageValue matches; else append.
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const newRow = result.data;
      queryClient.setQueryData<ListStageMappingsResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const filtered = prev.rows.filter(
          (r) => r.externalStageValue !== newRow.externalStageValue,
        );
        const next = [...filtered, newRow];
        next.sort((a, b) =>
          a.externalStageValue.toLowerCase().localeCompare(
            b.externalStageValue.toLowerCase(),
          ),
        );
        return { rows: next };
      });
    },
  });
}

export function useUpdateStageMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = stageMappingsQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof stageMappingService.updateStageMapping>[1];
    }) => stageMappingService.updateStageMapping(id, body),
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const updated = result.data;
      queryClient.setQueryData<ListStageMappingsResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const next = prev.rows.map((r) => (r.id === updated.id ? updated : r));
        return { rows: next };
      });
    },
  });
}

export function useDeleteStageMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = stageMappingsQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: stageMappingService.deleteStageMapping,
    onSuccess: (result, deletedId) => {
      if (result.error) return;
      queryClient.setQueryData<ListStageMappingsResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        return { rows: prev.rows.filter((r) => r.id !== deletedId) };
      });
    },
  });
}

// SCH-2.6: useReorderStageMappings REMOVED. 1:1 lookup has no order.

export function useStageVocabulary() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useQuery({
    queryKey: stageVocabularyQueryKey(tenantId),
    queryFn: async () => {
      const result = await stageMappingService.getStageVocabulary();
      if (result.error) {
        const err = new Error(result.error.message) as Error & {
          code?: string;
          details?: Record<string, unknown>;
        };
        err.code = result.error.code;
        err.details = result.error.details;
        throw err;
      }
      return result.data;
    },
    enabled: !!tenantId,
    // Vocabulary is cheap to read and changes infrequently (only on
    // Re-discover). 5min staleTime mirrors stage-mappings.
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
  });
}

export function useDiscoverStageValues() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useMutation({
    mutationFn: stageMappingService.discoverStageValues,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      // Splice the freshly-discovered vocabulary into the cache directly so
      // the tab's UI updates without a follow-up GET.
      queryClient.setQueryData<StageVocabulary>(
        stageVocabularyQueryKey(tenantId),
        result.data,
      );
    },
  });
}

/**
 * Set or clear the integration's default landing stage. Updates the
 * tenant-integration row directly (NOT a stage-mapping row), so we don't
 * mutate the stage-mappings cache here.
 */
export function useSetDefaultLandingStage() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  const queryKey = defaultLandingStageQueryKey(tenantId);
  return useMutation({
    mutationFn: stageMappingService.setDefaultLandingStage,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      queryClient.setQueryData(queryKey, result.data);
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', tenantId, 'integration'],
      });
    },
  });
}

export {
  defaultLandingStageQueryKey,
  stageMappingsQueryKey,
  stageVocabularyQueryKey,
};
